import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Users, DollarSign, Activity, TrendingUp, ShieldCheck } from "lucide-react";
import AdminStripeOps from "@/components/admin/AdminStripeOps";
import AdminStatusCard from "@/components/admin/AdminStatusCard";

const fmt = (n) => n ? "$" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "$0";

export default function Admin() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/admin/metrics").then(r => setMetrics(r.data)).catch(e => setError(e.response?.data?.detail || "Access denied"));
    api.get("/admin/users").then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const promote = async (id) => {
    if (!window.confirm("Grant admin access to this user?")) return;
    try { await api.post(`/admin/users/${id}/promote`); toast.success("Promoted"); const r = await api.get("/admin/users"); setUsers(r.data); }
    catch { toast.error("Failed"); }
  };

  if (error) return (
    <div className="p-10 max-w-6xl" data-testid="admin-access-denied">
      <div className="mb-6">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Admin console</div>
        <h1 className="font-display font-bold text-5xl tracking-tight">Admin</h1>
      </div>
      <AdminStatusCard />
      <div className="card-flat p-10 text-center">
        <ShieldCheck size={40} className="mx-auto mb-4 text-muted-foreground/60"/>
        <div className="text-muted-foreground">{error}</div>
        <div className="text-xs text-muted-foreground mt-3">
          Use the "Account status" card above to see why access is denied and how to fix it.
        </div>
      </div>
    </div>
  );
  if (!metrics) return <div className="p-10">Loading metrics...</div>;

  return (
    <div className="p-10 max-w-6xl" data-testid="admin-page">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Admin console</div>
        <h1 className="font-display font-bold text-5xl tracking-tight">Admin</h1>
        <p className="text-muted-foreground mt-2 text-sm">Signed in as {user?.email}</p>
      </div>

      <AdminStatusCard />

      {/* Revenue */}
      <div className="grid md:grid-cols-4 gap-px bg-border border border-border mb-6" data-testid="admin-revenue">
        <MetricCard icon={DollarSign} label="MRR (est.)" value={fmt(metrics.revenue.mrr_estimate_usd)} accent/>
        <MetricCard icon={DollarSign} label="Revenue · 30d" value={fmt(metrics.revenue.last_30d_usd)}/>
        <MetricCard icon={DollarSign} label="Revenue · total" value={fmt(metrics.revenue.total_usd)}/>
        <MetricCard icon={Activity} label="Paid txns" value={metrics.revenue.paid_transactions}/>
      </div>

      {/* Users */}
      <div className="grid md:grid-cols-4 gap-px bg-border border border-border mb-6" data-testid="admin-users-metrics">
        <MetricCard icon={Users} label="Total users" value={metrics.users.total}/>
        <MetricCard icon={Users} label="Pro users" value={metrics.users.pro} accent/>
        <MetricCard icon={TrendingUp} label="Conversion" value={metrics.users.conversion_pct + "%"}/>
        <MetricCard icon={Activity} label="Signups · 7d" value={metrics.signups.last_7d}/>
      </div>

      {/* Engagement */}
      <div className="grid md:grid-cols-2 gap-px bg-border border border-border mb-8" data-testid="admin-engagement">
        <MetricCard icon={Activity} label="WAU (chat)" value={metrics.engagement.weekly_active_users}/>
        <MetricCard icon={Activity} label="Apps tracked · 7d" value={metrics.engagement.apps_tracked_7d}/>
      </div>

      {/* Stripe ops — one-click price-map repair */}
      <AdminStripeOps />

      {/* Users list */}
      <div className="card-flat p-6" data-testid="admin-users-table">
        <div className="font-display font-bold text-xl mb-4">Users ({users.length})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left py-2 px-2">Email</th>
                <th className="text-left py-2 px-2">Name</th>
                <th className="text-left py-2 px-2">Tier</th>
                <th className="text-left py-2 px-2">Referrals</th>
                <th className="text-left py-2 px-2">Joined</th>
                <th className="text-right py-2 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-border" data-testid={`admin-user-${u.id}`}>
                  <td className="py-3 px-2 text-foreground/80 font-mono text-xs">{u.email}</td>
                  <td className="py-3 px-2 text-foreground/80">{u.name}</td>
                  <td className="py-3 px-2"><span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 ${u.subscription_tier === "pro" ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-300" : "bg-zinc-700/50 text-muted-foreground"}`}>{u.subscription_tier}</span></td>
                  <td className="py-3 px-2 text-muted-foreground font-mono">{u.referral_credits_months || 0}mo</td>
                  <td className="py-3 px-2 text-muted-foreground text-xs font-mono">{(u.created_at || "").slice(0,10)}</td>
                  <td className="py-3 px-2 text-right">
                    {!u.is_admin && <button data-testid={`admin-promote-${u.id}`} onClick={()=>promote(u.id)} className="text-xs text-primary hover:underline">Make admin</button>}
                    {u.is_admin && <span className="text-[10px] font-mono uppercase text-emerald-400">admin</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-background p-5">
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3"><Icon size={12}/> {label}</div>
      <div className={`font-display font-black text-3xl tracking-tight ${accent ? "text-emerald-400" : ""}`}>{value}</div>
    </div>
  );
}
